import { useState, useEffect, useCallback, useRef } from 'react';
import { subscribe, SHOW_TOAST, CLEAR_TOAST } from '../../../../utils/eventBuser';
import { POSITION } from '../constants';

const getDefaultToasts = () => {
    return Object.entries(POSITION).reduce((obj, [key, value]) => {
        obj[value] = [];
        return obj;
    }, {});
};

export const useToastContainer = ({
    position,
    autoClose,
    newestOnTop,
    useDefaultCloseButton,
    closeOnClick,
    containerId,
}) => {
    const [toasts, setToasts] = useState(getDefaultToasts());
    const toastNumberRef = useRef(1);

    const removeToast = useCallback((id) => {
        setToasts((prevToasts) =>
            Object.entries(prevToasts).reduce((obj, [key, value]) => {
                obj[key] = value.map((current) => {
                    if (current.id === id) current.isActive = false;
                    return current;
                });
                return obj;
            }, {}),
        );
    }, []);

    const removeAllToast = useCallback(() => {
        setToasts((prevToasts) =>
            Object.entries(prevToasts).reduce((obj, [key, value]) => {
                obj[key] = value.map((current) => {
                    current.isActive = false;
                    return current;
                });
                return obj;
            }, {}),
        );
    }, []);

    const clearDeletedToast = useCallback((id) => {
        setToasts((prevToasts) =>
            Object.entries(prevToasts).reduce((obj, [key, value]) => {
                obj[key] = value.filter((t) => t.id !== id);
                return obj;
            }, {}),
        );
    }, []);

    useEffect(() => {
        return subscribe(SHOW_TOAST, (toast) => {
            if (!toast) return;
            if (
                (!containerId && toast.containerId) ||
                (containerId && toast?.containerId !== containerId)
            )
                return;

            toast.isActive = true;

            if (!toast.position) toast.position = position;

            if (toast?.autoClose === undefined || toast?.autoClose === null) {
                toast.autoClose = autoClose;
            }
            if (
                toast?.useDefaultCloseButton === undefined ||
                toast?.useDefaultCloseButton === null
            ) {
                toast.useDefaultCloseButton = useDefaultCloseButton;
            }
            if (toast?.closeOnClick === undefined || toast?.closeOnClick === null) {
                toast.closeOnClick = closeOnClick;
            }
            toast.number = toastNumberRef.current;
            toastNumberRef.current++;

            const toastPosition = POSITION?.[toast.position] || null;
            if (!toastPosition) return;

            setToasts((prevToasts) => {
                const toastsByPosition = [...prevToasts[toastPosition]];

                if (newestOnTop) {
                    toastsByPosition.push(toast);
                } else {
                    toastsByPosition.unshift(toast);
                }

                return { ...prevToasts, [toastPosition]: toastsByPosition };
            });
        });
    }, [containerId, position, newestOnTop, autoClose, useDefaultCloseButton, closeOnClick]);

    useEffect(() => {
        return subscribe(CLEAR_TOAST, (props) => {
            if (!props?.id) return removeAllToast();
            removeToast(props.id);
        });
    }, [removeAllToast, removeToast]);

    return { toasts, setToasts, clearDeletedToast, removeToast };
};
